import { AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { codeInfo } from "@/lib/api/codes";
import { isApiError } from "@/lib/api/errors";

/** A failed call, in plain words. Uses the code table's title; shows the server's own sentence
 * when it adds something; keeps the raw code one click away for whoever is debugging. */
export function ErrorAlert({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const api = isApiError(error) ? error : null;
  const info = codeInfo(api?.code ?? "unknown_error");
  const extra = api?.detail && api.detail !== info.title ? api.detail : null;
  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle aria-hidden />
      <AlertTitle>{info.title}</AlertTitle>
      <AlertDescription>
        {extra && <p>{extra}</p>}
        {/* A blocked publish or send says what blocks it, not just that something does. */}
        {api && api.findings.length > 0 && (
          <ul className="list-disc pl-4">
            {api.findings.map((f, i) => (
              <li key={i}>{f.detail || f.code}</li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {onRetry && (
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw aria-hidden /> Try again
            </Button>
          )}
          {api && (
            <details className="text-xs">
              <summary className="cursor-pointer">Details</summary>
              <code className="font-mono">
                {api.status} {api.code}
              </code>
            </details>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}

/** The same plain words as a toast, for actions with no room for an inline alert. A 401 is left to
 * the session dialog. */
export function toastError(error: unknown) {
  if (isApiError(error) && error.status === 401) return;
  toast.error(codeInfo(isApiError(error) ? error.code : "unknown_error").title);
}
